import { assert } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import React from 'react';
import { createPortal } from 'react-dom';
import useDebugFeatures from '../../../../../hooks/use-debug-features';
import {
    CloseOutline,
    DownloadOutline,
    EditOutline,
    FavoriteOutline,
    FavoriteSolid,
    FileExportOutline,
    InfoCircleOutline,
    TrashOutline,
    UserGroupOutline,
    UserOutline
} from '../../../../../icons/icons';
import { UdViewModel, UDViewType } from '../../../../../udView/udview';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { deleteView, importViews, updateView } from '../../../../state/slices/views-slice';
import { workspaceSwitchView } from '../../../../state/slices/workspace-slice';
import { store } from '../../../../state/store';
import { useCanSaveView, useFocusedWorkspaceSelector } from '../../hooks/use-view';
import './UdViewsList.css';

export function UdViewsList(props: { onEdit?: (view: UdViewModel) => void }) {
    const [exportActive, setExportActive] = React.useState(false);
    const [exportedViews, setExportedViews] = React.useState([] as string[]);
    const canEditCompanyViews = useAppSelector((x) => x.user.rights.canEditCompanyViews);
    const queryForUnsavedChanges = useAppSelector((x) => x.settings.queryToSaveViewIfChanged);
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const [showSaveConfirmation, setShowSaveConfirmation] = React.useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState<UdViewModel | null>(null);
    const selectedViewId = useFocusedWorkspaceSelector((x) => x.view.id);
    const selectedViewName = useFocusedWorkspaceSelector((x) => x.view.name);
    const selectedViewModified = useFocusedWorkspaceSelector((x) => x.viewModified);
    const views = useAppSelector((state) => state.views.items);
    const dispatch = useAppDispatch();
    const [nextViewToSwitch, setNextViewToSwitch] = React.useState<UdViewModel | null>(null);
    const debug = useDebugFeatures();
    const [detailsView, setDetailsView] = React.useState<UdViewModel | null>(null);
    const busy = useAppSelector((x) => x.views.busy);
    const canSaveView = useCanSaveView();

    const handleSelectView = (view: UdViewModel) => {
        if (queryForUnsavedChanges && canSaveView) {
            setShowSaveConfirmation(true);
            setNextViewToSwitch(view);
        } else {
            dispatch(
                workspaceSwitchView({
                    workspaceId: focusedWorkspaceId,
                    view: view
                })
            );
        }
    };

    const handleSaveConfirm = async (save: boolean) => {
        assert(nextViewToSwitch);
        if (save) {
            const workspace = store.getState().workspace;
            const view = workspace.items[workspace.selectedId].view;
            dispatch(updateView({ view, workspaceId: focusedWorkspaceId }));
        }

        dispatch(
            workspaceSwitchView({
                workspaceId: focusedWorkspaceId,
                view: nextViewToSwitch
            })
        );
        setShowSaveConfirmation(false);
        setNextViewToSwitch(null);
    };

    const handleSaveCancel = () => {
        setShowSaveConfirmation(false);
        setNextViewToSwitch(null);
    };
    const handleCancelExport = () => {
        setExportActive(false);
        setExportedViews([]);
    };

    const handleExportClicked = async () => {
        if (exportActive) {
            if (window.showSaveFilePicker) {
                const file = await window.showSaveFilePicker({
                    suggestedName: 'views',
                    types: [{ accept: { 'application/json': '.json' } }]
                });
                if (!file) {
                    return;
                }

                const writable = await file.createWritable();
                const toExport = views.filter((x) => exportedViews.includes(x.id));
                const json = JSON.stringify(toExport, null, 2);
                await writable.write(json);
                await writable.close();
            }
            setExportActive(false);
            setExportedViews([]);
        } else {
            setExportActive(true);
        }
    };

    const handleImportClicked = async () => {
        try {
            if (window.showOpenFilePicker) {
                const [fileHandle] = await window.showOpenFilePicker({
                    multiple: false,
                    types: [{ accept: { 'application/json': '.json' } }]
                });

                if (!fileHandle) {
                    return;
                }

                const file = await fileHandle.getFile();
                const text = await file.text();
                const json = JSON.parse(text);

                if (!Array.isArray(json)) {
                    return;
                }

                const result = await dispatch(importViews({ views: json as UdViewModel[] })).unwrap();
                if (result.failedImports.length > 0) {
                    //
                }
            }
        } catch (err) {
            console.error(err);
            //
        }
    };

    const portalContainer = document.getElementById('ud-views-header-portal');

    return (
        <>
            {detailsView && (
                <Dialog title={detailsView.name ?? '??'} onClose={() => setDetailsView(null)}>
                    <pre style={{ height: '80vh', width: '75vw' }}>{JSON.stringify(detailsView, null, 2)}</pre>
                </Dialog>
            )}
            {showSaveConfirmation && (
                <Dialog title="Save changes" onClose={handleSaveCancel}>
                    <div>
                        You have made changes the <b>"{selectedViewName}"</b> view that have not yet been saved.
                    </div>
                    <div>Would you like to save these changes?</div>
                    <DialogActionsBar>
                        <Button onClick={() => handleSaveConfirm(false)}>No</Button>
                        <Button onClick={() => handleSaveConfirm(true)} themeColor={'primary'}>
                            Yes
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {showDeleteConfirmation && (
                <Dialog title="Delete view" onClose={() => setShowDeleteConfirmation(null)}>
                    <div>
                        Do you want to delete <strong>{showDeleteConfirmation.name}</strong> view?
                    </div>
                    <DialogActionsBar>
                        <Button disabled={busy} onClick={() => setShowDeleteConfirmation(null)}>
                            No
                        </Button>
                        <Button
                            disabled={busy}
                            onClick={async () => {
                                try {
                                    await dispatch(deleteView({ view: showDeleteConfirmation, workspaceId: focusedWorkspaceId })).unwrap();
                                } finally {
                                    setShowDeleteConfirmation(null);
                                }
                            }}
                            themeColor={'error'}>
                            Yes
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <div className="p-2" id="views-flyout">
                {debug &&
                    portalContainer &&
                    createPortal(
                        <>
                            {exportActive && (
                                <Button
                                    svgIcon={CloseOutline}
                                    className="button-small"
                                    fillMode="flat"
                                    title="Cancel export"
                                    onClick={handleCancelExport}></Button>
                            )}
                            <Button
                                svgIcon={FileExportOutline}
                                className="button-small"
                                fillMode="flat"
                                themeColor={exportActive ? 'primary' : undefined}
                                title="Export"
                                onClick={handleExportClicked}></Button>
                            <Button
                                svgIcon={DownloadOutline}
                                className="button-small"
                                fillMode="flat"
                                title="Import"
                                onClick={handleImportClicked}></Button>
                        </>,
                        portalContainer
                    )}
                <div className="d-flex flex-column">
                    {views.map((view) => {
                        const selected = selectedViewId === view.id;
                        const isDefault = !view.id;
                        return (
                            <div
                                className={clsx(
                                    'clickable d-flex justify-content-between align-items-center gap-0 ps-1',
                                    isDefault && 'default',
                                    view.id === window.tutorialViewId && 'last-saved'
                                )}
                                key={view.id}
                                style={{ borderRadius: 4 }}
                                onClick={() => handleSelectView(view)}>
                                {view.id && exportActive && (
                                    <Checkbox
                                        className="me-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (exportedViews.includes(view.id)) {
                                                setExportedViews(exportedViews.filter((x) => x !== view.id));
                                            } else {
                                                setExportedViews([...exportedViews, view.id]);
                                            }
                                        }}
                                        checked={exportedViews.includes(view.id)}
                                    />
                                )}

                                {view.id && <SvgIcon icon={view.type === UDViewType.company ? UserGroupOutline : UserOutline} />}

                                <span className={clsx('px-1 flex-grow-1 d-flex align-items-center gap-1', selected && 'fw-bold')}>
                                    {view.name}
                                    {selected && selectedViewModified && canSaveView && (
                                        <span
                                            style={{
                                                width: 6,
                                                height: 6,
                                                backgroundColor: 'var(--info-blue)',
                                                borderRadius: 3,
                                                display: 'inline-block'
                                            }}></span>
                                    )}
                                </span>

                                {debug && (
                                    <Button
                                        fillMode={'flat'}
                                        className="button-small view-tool-button"
                                        svgIcon={InfoCircleOutline}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDetailsView(view);
                                        }}
                                    />
                                )}
                                {!isDefault && (view.type !== UDViewType.company || canEditCompanyViews) && (
                                    <>
                                        <Button
                                            fillMode={'flat'}
                                            className="button-small view-edit-btn view-tool-button"
                                            svgIcon={EditOutline}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.onEdit?.(view);
                                            }}
                                        />

                                        <Button
                                            fillMode={'flat'}
                                            className="button-small view-delete-btn view-tool-button"
                                            svgIcon={TrashOutline}
                                            disabled={selected || busy}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowDeleteConfirmation(view);
                                            }}
                                        />
                                        <Button
                                            fillMode={'flat'}
                                            className="button-small view-fav-btn view-tool-button"
                                            svgIcon={view.isFavorite ? FavoriteSolid : FavoriteOutline}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(
                                                    updateView({
                                                        view: { ...view, isFavorite: !view.isFavorite },
                                                        workspaceId: focusedWorkspaceId
                                                    })
                                                );
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}
